import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './ControlVersions.module.scss';

const ControlVersionsComponent = () => {
  const {
    filters,
    fetch,
    controlVersions,
    setBreadcrumbs,
    fullSize,
    selectNode,
    setSelectNode,
    selectedKeys,
    setSelectedKeys,
    expandedKeys,
    setExpandedKeys,
    clear,
  } = controlVersionsStore;
  const { push } = useHistory();
  const { reportGroups } = reportGroupsStore;
  const { controls } = controlsStore;
  const { state } = useLocation();

  const permission = usePermission('CmControlVersion');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_CONTROL_VERSIONS);

  useAuditView('Версии контролей');

  useEffect(() => {
    fetch();
    fetchReferences(['reportGroups', 'controls']);
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(() => {
    const out = controlVersions
      .filter((el) => !(filters.reportGroup && el.repGroupCode !== filters.reportGroup))
      .map((item) => {
        const ctrlName = controls.find((control) => control.ctrlCode === item.ctrlCode)?.ctrlName;
        const repGroupName = reportGroups?.find((group) => group.code === item.repGroupCode)?.name;

        return {
          ...item,
          ctrlName,
          repGroupName,
        };
      });

    return toTreeView(out);
  }, [controlVersions, controls, filters.reportGroup, reportGroups]);

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  // при переходе к контролям из справочника Контролей, открываем иерархию по группе из которой осуществлялся переход
  useEffect(() => {
    if (state && state.ctrlCode) {
      const stateVersion = controlVersions.find((version) => version.ctrlCode === state.ctrlCode);

      if (stateVersion) {
        setExpandedKeys([stateVersion.repGroupCode ?? '']);
        onSelect(null, {
          node: { ...stateVersion, key: `${stateVersion.repGroupCode} - ${stateVersion.ctrlCode}` },
          selected: true,
        });
      }
    }
  }, [controlVersions, onSelect, setExpandedKeys, state]);

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROL_VERSIONS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () =>
      !selectNode && permission.CREATE ? (
        <Button onClick={onCreate} tooltip="Добавить">
          <Icon name="add" />
        </Button>
      ) : null,
    [onCreate, permission.CREATE, selectNode],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, { [styles.withTable]: selectNode, [styles.fullSize]: fullSize })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
        />
        <Table />
      </div>
    </DesignLayout>
  );
};

export const ControlVersions = observer(ControlVersionsComponent);
